import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useField, useFormikContext } from 'formik'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import {
  isNil,
  filter,
  length,
  gt,
  isEmpty,
  __,
  addIndex,
  map,
  forEach,
} from 'ramda'
import { useIntl } from 'react-intl'
import ImageUploadButton from './ImageUploadButton'
import apiConfig from 'app/configs/apiConfigs'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import useFeatureFlag from 'app/configs/useFeatureFlag'
const TypeFile = {
  IMAGE_TYPE: 'image',
}

const indexedMap = addIndex(map) // Creates an indexed map function

const DynamicTextInput = ({ className, name, disabled = false, onChange }) => {
  const formik = useFormikContext()
  const { mes_4367_attachment_gallery } = useFeatureFlag()
  const [field, meta, helpers] = useField({ name })
  const { setValue } = helpers
  const { formatMessage } = useIntl()
  const fieldValue = field.value || [
    { title: '', imagePath: '' },
    { title: '', imagePath: '' },
  ]

  const [charLimitErrors, setCharLimitErrors] = useState([])

  const isError = !isNil(meta.error) && meta.touched && !disabled

  const isHasOptionsError = gt(
    length(filter((item) => !isNil(item) && !isEmpty(item), charLimitErrors)),
    0,
  )

  const handleChange = useCallback(
    (indexValue, event) => {
      const newErrors = [...charLimitErrors]
      const previous = fieldValue[indexValue]
      if (!isEmpty(previous.title)) {
        const previousDuplicate = indexedMap(
          (item, index) =>
            item.title === previous.title && index !== indexValue
              ? index
              : null,
          fieldValue,
        ).filter((index) => index !== null)

        if (previousDuplicate.length === 1) {
          newErrors[previousDuplicate[0]] = null
        }
      }

      const value = event.target.value
      const currentDuplicate = indexedMap(
        (item, index) =>
          !isEmpty(item.title) && item.title === value ? index : null,
        fieldValue,
      ).filter((index) => index !== null)

      if (currentDuplicate.length > 0) {
        forEach((index) => {
          newErrors[index] = formatMessage({
            id: 'createNotification.validation.duplicate.poll.option',
          })
        }, currentDuplicate)

        newErrors[indexValue] = formatMessage({
          id: 'createNotification.validation.duplicate.poll.option',
        })
      } else if (value.length > 200) {
        newErrors[indexValue] = formatMessage(
          { id: 'createNotification.dialog.validate.maxLength.message' },
          { maxLength: value.length - 200 },
        )
      } else {
        newErrors[indexValue] = null
      }
      setCharLimitErrors(newErrors)

      const newInputs = [...fieldValue]
      newInputs[indexValue].title = value
      setValue(newInputs)

      if (onChange) {
        onChange(newInputs)
      }

      if (
        indexValue === fieldValue.length - 1 &&
        value !== '' &&
        fieldValue.length < 20
      ) {
        setValue([...newInputs, { title: '', imagePath: '' }])
      }
    },
    [fieldValue, setValue, onChange, charLimitErrors],
  )

  const onBodyInsertImage = (imageInfo) => {
    const images = formik.values.mediaSocials

    const getFileGif = imageInfo.path.match('.gif')
    if (isNil(getFileGif)) {
      const image = {
        id: (Math.random() * 100).toString(36).replace('.', ''),
        url: imageInfo.path,
        name: imageInfo.name,
        isSelected: isEmpty(images),
        type: TypeFile.IMAGE_TYPE,
      }
      images.push(image)
      formik.setFieldValue('mediaSocials', images)
    }
  }

  // Use refs to store the latest callbacks to avoid stale closures
  const onChangeRef = useRef(onChange)
  const setValueRef = useRef(setValue)

  useEffect(() => {
    onChangeRef.current = onChange
    setValueRef.current = setValue
  }, [onChange, setValue])

  const handleImageUpload = useCallback(
    (index, imagePath) => {
      // Get the latest field values from formik to avoid using stale data
      const currentFieldValue = formik.getFieldProps(name).value || [
        { title: '', imagePath: '' },
        { title: '', imagePath: '' },
      ]
      const newInputs = [...currentFieldValue]
      newInputs[index].imagePath = imagePath
      setValueRef.current(newInputs)

      if (!mes_4367_attachment_gallery && !isNil(imagePath)) {
        onBodyInsertImage({
          name: `${apiConfig.awsBucketUrl}/${imagePath}`,
          path: `${imagePath}`,
        })
      }

      if (onChangeRef.current) {
        onChangeRef.current(newInputs)
      }

      const isLastOption = index === newInputs.length - 1
      if (isLastOption && newInputs.length < 20 && !isNil(imagePath)) {
        setValueRef.current([...newInputs, { title: '', imagePath: '' }])
      }
    },
    [formik, name, mes_4367_attachment_gallery],
  )

  return (
    <>
      <div className="space-y-5">
        {fieldValue.map((inputValue, index) => {
          return (
            <div className={className} key={`option-${index + 1}`}>
              <div className="flex items-start space-x-2">
                <SimpleInput
                  fullWidth
                  type="text"
                  id={`option-${index + 1}`}
                  name={`option-${index + 1}`}
                  className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
                  placeholder={formatMessage({ id: 'action.add' })}
                  value={inputValue.title || ''}
                  onChange={(e) => handleChange(index, e)}
                  maxLength={200}
                  error={charLimitErrors[index]}
                  helperText={charLimitErrors[index]}
                  aria-invalid={isError ? 'true' : 'false'}
                />

                <ImageUploadButton
                  index={index}
                  imageUrl={inputValue.imagePath}
                  onImageUpload={handleImageUpload}
                />
              </div>
            </div>
          )
        })}
      </div>
      {isError && !isHasOptionsError && (
        <div className="flex space-x-2 py-1">
          <div className="flex my-auto">
            <ErrorOutlineIcon className="text-red-500" fontSize="small" />
          </div>
          <label className="text-red">{meta.error}</label>
        </div>
      )}
    </>
  )
}

export default DynamicTextInput
